import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, PhoneCall, Stethoscope, Compass, Languages, ClipboardCheck, HeartPulse, AlertCircle, FileText, Clock, TrendingDown, Hospital, ShieldCheck } from 'lucide-react';
const capabilities = [{
  icon: PhoneCall,
  name: '24/7/365 Nurse Access',
  description: 'Round-the-clock access to registered nurses — every shift, every weekend, every holiday.'
}, {
  icon: Stethoscope,
  name: 'Immediate Clinical Assessment',
  description: 'Evidence-based triage protocols guide each conversation to the right level of care.'
}, {
  icon: Compass,
  name: 'Care Navigation',
  description: 'Direct injured workers to the most appropriate care setting — self-care, clinic, urgent care, or ER.'
}, {
  icon: ClipboardCheck,
  name: 'OSHA Recordability Guidance',
  description: 'Clinical input on first-aid versus recordable injury determinations, in real time.'
}, {
  icon: Languages,
  name: 'Multilingual Support',
  description: 'On-demand interpretation in 200+ languages so every worker gets understood.'
}, {
  icon: AlertCircle,
  name: 'Red Flag Identification',
  description: 'Trained protocols immediately escalate potentially serious or catastrophic injuries.'
}, {
  icon: HeartPulse,
  name: 'Self-Care Education',
  description: 'For minor injuries, nurses provide first-aid instructions and a follow-up plan.'
}, {
  icon: FileText,
  name: 'Documentation & Reporting',
  description: 'Every call documented and shared with employer, claims, and provider stakeholders.'
}];
const benefits = [{
  icon: Hospital,
  title: 'Fewer ER visits',
  stat: 'Care',
  description: 'Reduction in unnecessary emergency room utilization through proper care navigation.'
}, {
  icon: ShieldCheck,
  title: 'Compliance & accuracy',
  stat: 'OSHA',
  description: 'OSHA-compliant documentation and recordability determinations on every call.'
}];
export function MedicalTriageNurse() {
  return <div className="bg-brand-paper" data-id="element-731" data-mp="3lke1fztz47ju">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-732" data-mp="3ll6xa5re9cn7">
        <div className="absolute inset-0 opacity-10" data-id="element-733" data-mp="3gqj8s6we4i8m">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-734" data-mp="3vgqt6s5ykbby"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-735" data-mp="8aq3ko7iet6v"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-736" data-mp="2a478vl863hyg">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-737" data-mp="2a69xq1xl351l">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-738" data-mp="pnzeyag03h0f">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-739" data-mp="fvjxrfcj2wgl">
                First Response Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-740" data-mp="6vng3xok2xv1">
              Medical <span className="text-brand-gold italic" data-id="element-741" data-mp="3p2bj9i01pay2">Triage</span>{' '}
              Nurse
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-742" data-mp="3t3myctlamds0">
              The first call after an injury should be to a nurse — not an
              emergency room. 24/7 telephonic triage that guides injured workers
              to the right level of care from the very first minute.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-743" data-mp="12irrhv8avqug">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-744" data-mp="xmjasdegfpl6">
                Activate the Service <ArrowRight className="w-5 h-5 ml-2" data-id="element-745" data-mp="jm053cxgovm6" />
              </Link>
              <a href="tel:7142591053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-746" data-mp="jo33olcojx2i">
                <Phone className="w-5 h-5 mr-2" data-id="element-747" data-mp="4x3zdmivnuoq" /> 714.259.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-748" data-mp="3w2ls4d9a5xvt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-749" data-mp="3s1a0612xp0kg">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-750" data-mp="27kamccqj8qbz">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-751" data-mp="2lir2euaabt6s">
              Why It Matters
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-752" data-mp="1sulfc0vrds3s">
              The first hour shapes the entire claim.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-753" data-mp="1xsmz6o10dvgb">
              When an injury happens, the default response is often "go to the
              ER." But research shows that the majority of workplace injuries
              can be safely managed with self-care or a clinic visit — saving
              thousands of dollars per claim and getting workers back to work
              faster.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-754" data-mp="121bauy9t43fp">
              Our Medical Triage Nurse service puts an experienced RN on the
              phone immediately after an injury. We assess, advise, navigate,
              and document — turning the most chaotic moment of a claim into the
              most clinically-informed.
            </p>
            <ul className="space-y-4" data-id="element-755" data-mp="j5l8vb1vj71t">
              {['24/7/365 access to RN-level clinical care', 'Evidence-based triage protocols', 'Reduces unnecessary ER and urgent care utilization', 'OSHA recordability support in real time'].map(item => <li key={item} className="flex items-start" data-id="element-756" data-mp="o697oiwcgs6l">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-757" data-mp="24guze4zuheeh" />
                  <span className="text-gray-800 font-medium" data-id="element-758" data-mp="1zi9ye3crqqoz">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-759" data-mp="3mfrlgkid6kd4">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-760" data-mp="51e4ylq95nx6"></div>
            <img src="https://cdn.magicpatterns.com/uploads/gh5Eqe1fwFg2HJhUnvPV5u/Depositphotos_experienced_nurse.jpg" alt="Experienced triage nurse smiling while taking a call at her desk" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-761" data-mp="92zcdrk0p9zz" />
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-762" data-mp="sqyob5qbin6z">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-763" data-mp="2osa6cybfk55b">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-764" data-mp="2k1aalwe1rt4e">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-765" data-mp="2jzpi0cbamk96">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-766" data-mp="2a3e1190rd68e">
              Built for the moment injuries happen.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-767" data-mp="37qcpwxl7l9vq">
              Every triage call is supported by evidence-based protocols,
              multilingual access, and full documentation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-768" data-mp="2xqvowtgwjk63">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-769" data-mp="1meb9n9gbnz0p" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-770" data-mp="gmq40pvrg061">
                    <Icon className="w-6 h-6" data-id="element-771" data-mp="pj8wk102xm23" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-772" data-mp="ujmuurbvphmx">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-773" data-mp="2wbjhavzl4bwh">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-774" data-mp="24zn4v6lsv9f2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-775" data-mp="29xyb77cvz1sj">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-776" data-mp="29wn879r9ly2h">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-777" data-mp="2exb7eipndl7s">
              Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-778" data-mp="2iyd0ehsz111b">
              The right care from minute one.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-779" data-mp="2so69nc5n542e">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-780" data-mp="32mm3vnzystg4" data-mpi={i}>
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-781" data-mp="1eouzvrllmyy0"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-782" data-mp="19p8e2trpgadl" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-783" data-mp="19s4auertgt0g">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-784" data-mp="100qicqya8tgt">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-785" data-mp="1xfciv4gmqg66">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-786" data-mp="3mxrk2riol907">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-787" data-mp="3qybcxiw81qgj"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-788" data-mp="3d6wt59uhpl8f">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-789" data-mp="3i5rkx1afqk4o">
            Put a nurse on the first call.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-790" data-mp="337phqs7bg0ny">
            Let's set up 24/7 nurse triage for your program. We'll show you how
            to launch and the savings you can expect from day one.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-791" data-mp="2u9vpptgl3t6l">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-792" data-mp="2z75klomqsx1t">
              Activate Triage
            </Link>
            <a href="tel:7142591053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-793" data-mp="17io2k7rl8vdt">
              <Phone className="w-5 h-5 mr-2" data-id="element-794" data-mp="1gdvpremtd1gn" /> 714.259.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}